import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import styled from "styled-components";
import PropTypes from "prop-types";

import { useViewport } from "../../hooks/useViewport";
import {
    ProfileMenuDesktop,
    Wrapper as DesktopMenuWrapper,
    Content as DesktopContent
} from "../ProfileMenuDesktop";
import { ProfileMenuMobile } from "../ProfileMenuMobile";
import { LoggedInLinks } from "./LoggedInLinks";
import { LoggedOutLinks } from "./LoggedOutLinks";

import {
    Div,
    scrollToDesktopWithHero,
    scrollToDesktopNoHero,
    breakpoints
} from "shared";
import { Wrapper } from "./styles";

let ProfileMenu = ({
    currentUser,
    loginLink,
    joinLink,
    handleLogOut,
    isHeroSearchV2
}) => {
    const [profileMenuIsOpen, setProfileMenuIsOpen] = useState(false);
    const toggleProfileMenu = () => setProfileMenuIsOpen(!profileMenuIsOpen);
    const closeProfileMenu = () => setProfileMenuIsOpen(false);

    const trigger = useRef();
    const desktopMenu = useRef();
    const mobileMenu = useRef();

    const loggedOut = !currentUser || currentUser.isAnonymous;

    const { browserWidth } = useViewport();
    const mobileBreakpoint = breakpoints.beforeTabletPx;

    useEffect(() => {
        const handleClick = e => {
            const clickedInsideDesktopMenu =
                desktopMenu && desktopMenu.current.contains(e.target);
            const clickedInsideMobileMenu =
                mobileMenu && mobileMenu.current.contains(e.target);
            const clickedTrigger =
                trigger && trigger.current.contains(e.target);

            if (
                clickedInsideMobileMenu ||
                clickedInsideDesktopMenu ||
                clickedTrigger
            ) {
                return;
            }

            closeProfileMenu();
        };

        if (profileMenuIsOpen) {
            document.addEventListener("mousedown", handleClick);
            if (browserWidth < mobileBreakpoint) {
                document.querySelector("body").style.overflow = "hidden";
            }
        }

        return () => {
            document.removeEventListener("mousedown", handleClick);
            if (browserWidth < mobileBreakpoint) {
                document.querySelector("body").style.overflow = "auto";
            }
        };
    }, [profileMenuIsOpen]);

    // this is to change the header styles on scroll when it is a hero search w/ dark background:
    const scrollPosition = isHeroSearchV2
        ? scrollToDesktopWithHero
        : scrollToDesktopNoHero;

    const handleScroll = () => {
        if (desktopMenu && desktopMenu.current) {
            if (window.pageYOffset > scrollPosition) {
                isHeroSearchV2 &&
                    desktopMenu.current.classList.add("scrolledDown");
                isHeroSearchV2 &&
                    desktopMenu.current.classList.remove("scrolledUp");
            } else if (window.pageYOffset < scrollPosition) {
                isHeroSearchV2 &&
                    desktopMenu.current.classList.remove("scrolledDown");
                isHeroSearchV2 &&
                    desktopMenu.current.classList.add("scrolledUp");
            }
        }
    };

    useLayoutEffect(() => {
        window.addEventListener("scroll", handleScroll);

        return () => {
            window.removeEventListener("scroll", () => handleScroll);
        };
    }, []);

    // Wrap handleLogOut here so we can close the profile menu. This is a little
    // odd, but probably easier than the alternatives. Otherwise, if you click
    // "Log Out", then the back button, the profile menu can still be showing.
    const closeMenuAndHandleLogOut = () => {
        closeProfileMenu();
        handleLogOut();
    };

    return (
        <Wrapper open={profileMenuIsOpen} isHeroSearchV2={isHeroSearchV2}>
            {loggedOut ? (
                <LoggedOutLinks loginLink={loginLink} joinLink={joinLink} />
            ) : (
                <Div onClick={toggleProfileMenu} ref={trigger}>
                    <LoggedInLinks
                        userAvatarTitle={currentUser && currentUser.username}
                        userAvatarUrl={currentUser && currentUser.avatarUrl}
                        active={profileMenuIsOpen}
                    />
                </Div>
            )}
            <ProfileMenuDesktop open={profileMenuIsOpen}>
                <DesktopMenuWrapper
                    ref={desktopMenu}
                    isHeroSearchV2={isHeroSearchV2}
                >
                    <DesktopContent
                        logOut={closeMenuAndHandleLogOut}
                        user={currentUser}
                        closeMenu={closeProfileMenu}
                        isHeroSearchV2={isHeroSearchV2}
                    />
                </DesktopMenuWrapper>
            </ProfileMenuDesktop>
            <div ref={mobileMenu}>
                <ProfileMenuMobile
                    logOut={closeMenuAndHandleLogOut}
                    user={currentUser}
                    closeMenu={closeProfileMenu}
                />
            </div>
        </Wrapper>
    );
};

ProfileMenu.propTypes = {
    currentUser: PropTypes.object,
    loginLink: PropTypes.oneOfType([PropTypes.node, PropTypes.element])
        .isRequired,
    joinLink: PropTypes.oneOfType([PropTypes.node, PropTypes.element])
        .isRequired,
    handleLogOut: PropTypes.func.isRequired,
    isHeroSearchV2: PropTypes.bool
};

ProfileMenu = styled(ProfileMenu)``;
export { ProfileMenu };
